import { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { sessionConfirm, sessionService } from '../services/sessionService';
import {
  queueEmail as queueEmailAction,
  readyToBook,
  sessionId as sessionIdAction,
  setIsExistUserInDB,
} from '../store/queue/actions';

export function useSessionGuard(
  isStartQueue: boolean,
  sessionId: string,
  useQueue: boolean,
  queueEmail: string | null
) {
  const [position, setPosition] = useState<number | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);

  const dispatch = useDispatch();

  const clearQueueData = () => {
    dispatch(sessionIdAction(null));
    dispatch(queueEmailAction(null));
    dispatch(setIsExistUserInDB(false));
    sessionService.clearSession();
  };

  useEffect(() => {
    if (!isStartQueue || !sessionId || !queueEmail) return;

    let intervalId: NodeJS.Timeout | null = null;

    const checkSession = async () => {
      let session = sessionService.getSession();

      if (!session) {
        session = sessionService.createSession(queueEmail);
        sessionService.updateSession({ ...session, id: sessionId });
      }

      const status = await sessionService.checkSessionStatus(sessionId, queueEmail);
      const { blocked, position, isReady, userExist } = status;

      if (blocked) {
        setIsBlocked(true);
        clearInterval(intervalId);
        return;
      }

      session.isReady = isReady;
      session.queuePosition = position;
      sessionService.updateSession(session);

      setPosition(position);

      if (isReady) {
        try {
          await sessionConfirm(session.id, queueEmail);
          dispatch(readyToBook(true));
          dispatch(setIsExistUserInDB(userExist));
          clearInterval(intervalId);
        } catch (e) {
          console.error(e);
        }
      }
    };

    intervalId = setInterval(checkSession, 5000);
    checkSession().catch((e) => {
      clearQueueData();
      console.error(e);
    });

    return () => {
      clearInterval(intervalId);
    };
  }, [isStartQueue, sessionId, queueEmail]);

  useLayoutEffect(() => {
    if (!useQueue) return;

    const clearDataListener = () => {
      const isCheckoutPage = window.location.pathname.includes('/checkout');
      const isBookPage = window.location.pathname.includes('/book');
      if (isCheckoutPage || isBookPage) return;

      clearQueueData();
    };
    const isOnIOS = navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPhone/i);
    const event = isOnIOS ? 'pagehide' : 'beforeunload';
    addEventListener(event, clearDataListener);

    return () => {
      removeEventListener('beforeunload', clearDataListener);
    };
  }, [useQueue]);

  return { position, isBlocked };
}
